import { type FC, useState } from "react";

import { type Member, ModelType, type StructureApiServiceModelDescriptor } from "@doitintl/cmp-models";
import { Stack } from "@mui/system";
import { type FieldInputProps, useFormikContext } from "formik";
import upperFirst from "lodash/upperFirst";

import { AddParametersButton } from "../AddParametersButton";
import { GenericForm } from "../ApiActionParametersForm";
import { type ComponentResolver } from "../resolvers/ComponentResolver";
import { getInitialValueForModel } from "../useApiActionParametersSchema";
import { GuidingLineConnector } from "./common/GuidingLineConnector";
import { useStructureFields } from "./common/useStructureFields";
import { Fieldset } from "./wrappers/Fieldset";

export const StructureParam: FC<{
  fieldPath: string;
  fieldProps: FieldInputProps<string>;
  label: string;
  onRemove?: () => void;
  inputModel: StructureApiServiceModelDescriptor<Member>;
  isListItem?: boolean;
  rootPath?: string;
  resolver?: ComponentResolver;
}> = ({ fieldPath, fieldProps, label, onRemove, inputModel, isListItem, rootPath, resolver }) => {
  const formikProps = useFormikContext();
  const [hasVisibleButton, setHasVisibleButton] = useState(false);
  const [members, addMember, removeMember] = useStructureFields(
    Object.entries(fieldProps.value)
      .filter(([_, v]) => v !== undefined)
      .map(([k]) => k),
    inputModel.requiredMembers
  );

  const getMemberPath = (memberName: string) => (fieldPath ? `${fieldPath}.${memberName}` : memberName);

  const createRemoveFn = (memberName: string) => () => {
    formikProps.setFieldValue(getMemberPath(memberName), undefined);
    removeMember(memberName);
  };

  const requiredMembers = (inputModel.requiredMembers?.length ?? 0) > 1;
  const isNotTopLevel = (!rootPath && fieldPath) || (rootPath && fieldPath !== rootPath);
  const showConnector = hasVisibleButton && isNotTopLevel;
  return (
    <Fieldset label={label} onRemove={onRemove} nestedInList={hasVisibleButton} isListItem={isListItem}>
      <Stack spacing={2}>
        {members.map((memberName) => {
          const memberPath = getMemberPath(memberName);
          const onRemove = inputModel.requiredMembers?.includes(memberName) ? undefined : createRemoveFn(memberName);
          const isNestedField = [ModelType.STRUCTURE, ModelType.LIST, ModelType.MAP, ModelType.UNION].includes(
            inputModel.members[memberName].model.type
          );
          const isIndented = !isNestedField && requiredMembers && !hasVisibleButton && isNotTopLevel;

          return (
            <Stack key={memberName} direction="column" ml={isIndented ? -2 : 0}>
              {showConnector && <GuidingLineConnector sx={{ top: 24 }} />}
              <GenericForm
                inputModel={inputModel.members[memberName].model}
                fieldPath={memberPath}
                label={inputModel.members[memberName].label ?? upperFirst(memberName)}
                onRemove={onRemove}
                resolver={resolver}
              />
            </Stack>
          );
        })}
        <AddParametersButton
          alreadyAdded={members}
          label={label}
          members={inputModel.members}
          onMembersAdd={(membersToAdd) => {
            membersToAdd.forEach((memberNameToAdd) => {
              const memberToAddPath = getMemberPath(memberNameToAdd);
              const memberToAddDefaultValue = getInitialValueForModel(inputModel.members[memberNameToAdd].model);
              formikProps.setFieldValue(memberToAddPath, memberToAddDefaultValue);
              addMember(memberNameToAdd);
            });
          }}
          onButtonStateChange={setHasVisibleButton}
        />
      </Stack>
    </Fieldset>
  );
};
